import { combineReducers, configureStore, createAction } from '@reduxjs/toolkit';
import loginSlice from './slices/Auth/login';
import forgotPasswordSlice from './slices/Auth/forgotPassword';
import resetPasswordSlice from './slices/Auth/resetPassword';
import otpVerificationSlice from './slices/Auth/otpVerification';
import kcDcmTokenSlice from './slices/Auth/kc-dcm-token';
import addClinicSlice from './slices/clinics/addClinic';
import getClinicSlice from './slices/clinics/getClinic';
import deleteClinicSlice from './slices/clinics/deleteClinic';
import editClinicSlice from './slices/clinics/editClinic';
import addUserSlice from './slices/users/addUser';
import editUserSlice from './slices/users/editUser';
import getUserSlice from './slices/users/getUser';
import getCardiologistsSlice from './slices/users/getCardiologists';
import getSonographersSlice from './slices/users/getSonographers';
import deleteUserSlice from './slices/users/deleteUser';
import getProfileDetailsSlice from './slices/profile/getProfileDetails';
import updateProfileDetailsSlice from './slices/profile/updateProfileDetails';
import getCommonDataSlice from './slices/Common/getCommonData';
import getHospitalDoctorsListSlice from './slices/reports/getHospitalDoctorsList';
import getHospitalReportsListSlice from './slices/reports/getHospitalReportsList';
import assignReportToDoctorSlice from './slices/reports/assignReportToDoctor';
import setReportSonographerSlice from './slices/reports/setReportSonographer';
import changePasswordSlice from './slices/Auth/changePassword';
import reportTemplateApiSlice from './slices/clinics/reportTemplate';
import getReportTemplateApiSlice from './slices/clinics/getReportTemplate';
import uploadClinicLogoApiSlice from './slices/clinics/uploadClinicLogo';
import getReportTemplatesSlice from './slices/reports/getReportTemplates';
import createReportSlice from './slices/reports/createReport';
import createReportTemplateSlice from './slices/reports/createReportTemplate';

import getCurrentReportSlice from './slices/reports/getCurrentReport';
import fetchDoctorsToAssignSlice from './slices/reports/fetchDoctorsToassign';
import changeReportStatusSlice from './slices/reports/changeReportStatus';
import regenerateReportSlice from './slices/reports/regenerateReport';
import deleteReportSlice from './slices/reports/deleteReport';
import updateReportSlice from './slices/reports/updateReport';
import getReportDataByIdSlice from './slices/reports/getReportDataById';
import getStressValuesSlice from './slices/reports/getStressValues';
import volumeROIDataSlice from './slices/Measurements/volumeROIData';
import getDashboardDataSlice from './slices/dashboard/getDashboardData';
import getInsightsSlice from './slices/dashboard/getInsights';
import addPacsSlice from './slices/pacs/addPacs';
import getPacsDataSlice from './slices/pacs/getPacs';
import editPacsDataSlice from './slices/pacs/editPacs';
import deletePacsApiSlice from './slices/pacs/deletePacs';
import updateReportTemplateSlice from './slices/reports/updateReportTemplate';
import getReportTemplatesByIdSlice from './slices/reports/getReportTemplateById';
import getDefaultReportTemplateSlice from './slices/reports/getDefaultReportTemplate';
import getAuditLogsSlice from './slices/auditLogs/getAuditLogs';
import uploadRelatedReportSlice from './slices/relatedReports/uploadRelatedReport';
import getRelatedReportSlice from './slices/relatedReports/getRelatedReport';
import deleteRelatedReportSlice from './slices/relatedReports/deleteRelatedReport';
import getReportFromPatientIdSlice from './slices/reports/getReportFromPatientId';
import getAllReportByStudyInstanceSlice from './slices/reports/getAllReportByStudyInstance';
import addAdminSlice from './slices/clinics/addAdmin';
import getAlertSettingsSlice from './slices/clinics/getAlertSettings';
import updateAlertSettingsSlice from './slices/clinics/updateAlertSettings';
import getNotificationsSlice from './slices/reports/getNotifications';
import getStudyMarkingSlice from './slices/reports/getStudyMarking';
import addStudyMarkingSlice from './slices/reports/addStudyMarking';
import removeStudyMarkingSlice from './slices/reports/removeStudyMarking';
import addStudyTopicSlice from './slices/reports/addStudyTopic';
import getStudyTopicsSlice from './slices/reports/getStudyTopics';
import multipleHospitalsSlice from './slices/Auth/multipleHospitals';
import downloadPdfSlice from './slices/reports/downloadPdf';
import getPrefillUserSlice from './slices/users/prefillUser';
import getUserDocSlice from './slices/Auth/getUserDoc';
import getImageUrlsSlice from './slices/express/getImageUrls';
import sendHL7Slice from './slices/hl7/sendHL7';
import getLocalProvidersSlice from './slices/hl7/getLocalProviders';
import lookupProviderByNameSlice from './slices/hl7/lookupProviderByName';
import lookupProviderByIdSlice from './slices/hl7/lookupProviderById';
import saveDraftDistributionSlice from './slices/hl7/saveDraftDistribution';
import sendDraftDistributionSlice from './slices/hl7/sendDraftDistribution';
import deleteDraftDistributionSlice from './slices/hl7/deleteDraftDistribution';
import getLiteImagesSlice from './slices/reports/getLiteImages';
import updateReportClipsSlice from './slices/reports/updateReportClips';
import updatePatientNameSlice from './slices/reports/updatePatientName';
import getPatientNameOverridesSlice from './slices/reports/getPatientNameOverrides';
import getDropdownSettingsSlice from './slices/clinics/getDropdownSettings';
import createDropdownSettingSlice from './slices/clinics/createDropdownSetting';
import updateDropdownSettingSlice from './slices/clinics/updateDropdownSetting';
import deleteDropdownSettingSlice from './slices/clinics/deleteDropdownSetting';
import sendEmailDistributionSlice from './slices/distribution/sendEmailDistribution';
import saveEmailDraftSlice from './slices/distribution/saveEmailDraft';
import getClinicFeaturesSlice from './slices/clinics/getClinicFeatures';
import getFeatureControlSlice from './slices/clinics/getFeatureControl';
import updateFeatureControlSlice from './slices/clinics/updateFeatureControl';

/**
 * Dispatching this action resets every slice in the Redux store back to its
 * initialState.  It works by passing `undefined` as the state to the combined
 * reducer — each individual slice reducer then returns its own initialState.
 *
 * Must be dispatched on every logout / session-switch so that no data from the
 * previous user leaks into the next session.
 */
export const RESET_STORE = createAction('RESET_STORE');

const appReducer = combineReducers({
  loginResponse: loginSlice,
  forgotPasswordResponse: forgotPasswordSlice,
  resetPasswordResponse: resetPasswordSlice,
  otpVerificationResponse: otpVerificationSlice,
  changePasswordResponse: changePasswordSlice,
  kcDcmTokenResponse: kcDcmTokenSlice,
  addClinicResponse: addClinicSlice,
  getClinicResponse: getClinicSlice,
  deleteClinicResponse: deleteClinicSlice,
  editClinicResponse: editClinicSlice,
  addUserResponse: addUserSlice,
  editUserResponse: editUserSlice,
  getUserResponse: getUserSlice,
  getCardiologistsResponse: getCardiologistsSlice,
  getSonographersResponse: getSonographersSlice,
  deleteUserResponse: deleteUserSlice,
  getProfileDetailsResponse: getProfileDetailsSlice,
  updateProfileDetailsResponse: updateProfileDetailsSlice,
  reportTemplateApiResponse: reportTemplateApiSlice,
  getReportTemplatesResponse: getReportTemplatesSlice,
  getCommonDataSliceResponse: getCommonDataSlice,
  getHospitalDoctorsListResponse: getHospitalDoctorsListSlice,
  getHospitalReportsListResponse: getHospitalReportsListSlice,
  assignReportToDoctorResponse: assignReportToDoctorSlice,
  setReportSonographerResponse: setReportSonographerSlice,
  getReportTemplateApiResponse: getReportTemplateApiSlice,
  uploadClinicLogoResponse: uploadClinicLogoApiSlice,
  createReportResponse: createReportSlice,
  createReportTemplateResponse: createReportTemplateSlice,
  getCurrentReportResponse: getCurrentReportSlice,
  fetchDoctorsToAssignResponse: fetchDoctorsToAssignSlice,
  changeReportStatusResponse: changeReportStatusSlice,
  regenerateReportResponse: regenerateReportSlice,
  deleteReportResponse: deleteReportSlice,
  updateReportResponse: updateReportSlice,
  getReportDataByIdResponse: getReportDataByIdSlice,
  getStressValuesResponse: getStressValuesSlice,
  volumeROIData: volumeROIDataSlice,
  getDashboardDataResponse: getDashboardDataSlice,
  getInsightsResponse: getInsightsSlice,
  addPacsResponse: addPacsSlice,
  getPacsDataResponse: getPacsDataSlice,
  editPacsDataResponse: editPacsDataSlice,
  deletePacsApiResponse: deletePacsApiSlice,
  updateReportTemplateResponse: updateReportTemplateSlice,
  getReportTemplatesByIdResponse: getReportTemplatesByIdSlice,
  getDefaultReportTemplateResponse: getDefaultReportTemplateSlice,
  getAuditLogsResponse: getAuditLogsSlice,
  uploadRelatedReportResponse: uploadRelatedReportSlice,
  getRelatedReportResponse: getRelatedReportSlice,
  deleteRelatedReportResponse: deleteRelatedReportSlice,
  getReportFromPatientIdResponse: getReportFromPatientIdSlice,
  getAllReportByStudyInstanceResponse: getAllReportByStudyInstanceSlice,
  addAdminResponse: addAdminSlice,
  getAlertSettingsResponse: getAlertSettingsSlice,
  updateAlertSettingsResponse: updateAlertSettingsSlice,
  getNotificationsResponse: getNotificationsSlice,
  getStudyMarkingResponse: getStudyMarkingSlice,
  addStudyMarkingResponse: addStudyMarkingSlice,
  removeStudyMarkingResponse: removeStudyMarkingSlice,
  addStudyTopicResponse: addStudyTopicSlice,
  getStudyTopicsResponse: getStudyTopicsSlice,
  multipleHospitalsResponse: multipleHospitalsSlice,
  downloadPdfResponse: downloadPdfSlice,
  getPrefillUserResponse: getPrefillUserSlice,
  getUserDocResponse: getUserDocSlice,
  getImageUrlsResponse: getImageUrlsSlice,
  sendHL7Response: sendHL7Slice,
  getLocalProvidersResponse: getLocalProvidersSlice,
  lookupProviderByNameResponse: lookupProviderByNameSlice,
  lookupProviderByIdResponse: lookupProviderByIdSlice,
  saveDraftDistributionResponse: saveDraftDistributionSlice,
  sendDraftDistributionResponse: sendDraftDistributionSlice,
  deleteDraftDistributionResponse: deleteDraftDistributionSlice,
  getLiteImagesResponse: getLiteImagesSlice,
  updateReportClipsResponse: updateReportClipsSlice,
  updatePatientNameResponse: updatePatientNameSlice,
  getPatientNameOverridesResponse: getPatientNameOverridesSlice,
  getDropdownSettingsResponse: getDropdownSettingsSlice,
  createDropdownSettingResponse: createDropdownSettingSlice,
  updateDropdownSettingResponse: updateDropdownSettingSlice,
  deleteDropdownSettingResponse: deleteDropdownSettingSlice,
  sendEmailDistributionResponse: sendEmailDistributionSlice,
  saveEmailDraftResponse: saveEmailDraftSlice,
  getClinicFeaturesResponse: getClinicFeaturesSlice,
  getFeatureControlResponse: getFeatureControlSlice,
  updateFeatureControlResponse: updateFeatureControlSlice,
});

/**
 * Root reducer that intercepts RESET_STORE.  Passing `undefined` as state
 * causes every slice reducer to return its own initialState, giving us a
 * clean store without having to touch any individual slice file.
 */
const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: any) => {
  if (action.type === RESET_STORE.type) {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
});
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export default store;
